import { useSyncExternalStore } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import { parseEntryTimestamp } from "../../utils/customerEntryDates";

/**
 * One shared live subscription to `customer_entries` for the report list and the entry form,
 * so the collection is downloaded once instead of on every open / save.
 *
 * Row shape: { id, data, ts, voided } — `data` is read once per snapshot, `ts` is epoch ms (0 if unknown).
 */

const KEEP_ALIVE_MS = 60 * 1000;

let state = { rows: [], loading: true, error: "" };
const listeners = new Set();
let unsubscribe = null;
let idleTimer = null;

function emit(next) {
  state = next;
  listeners.forEach((l) => l());
}

function start() {
  if (unsubscribe) return;
  unsubscribe = onSnapshot(
    collection(db, "customer_entries"),
    (snap) => {
      const rows = snap.docs.map((d) => {
        const data = d.data({ serverTimestamps: "estimate" }) || {};
        return {
          id: d.id,
          data,
          ts: parseEntryTimestamp(data)?.getTime() ?? 0,
          voided: data.voided === true,
        };
      });
      rows.sort((a, b) => b.ts - a.ts);
      emit({ rows, loading: false, error: "" });
    },
    (err) => {
      console.error("customer_entries", err);
      emit({ ...state, loading: false, error: err?.message || "Could not load entries." });
    }
  );
}

function stop() {
  if (unsubscribe) unsubscribe();
  unsubscribe = null;
  state = { rows: [], loading: true, error: "" };
}

function subscribe(listener) {
  listeners.add(listener);
  if (idleTimer) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
  start();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      idleTimer = setTimeout(() => {
        idleTimer = null;
        if (listeners.size === 0) stop();
      }, KEEP_ALIVE_MS);
    }
  };
}

function getSnapshot() {
  return state;
}

/** @returns {{ rows: Array<{ id: string, data: Record<string, any>, ts: number, voided: boolean }>, loading: boolean, error: string }} */
export function useCustomerEntries() {
  return useSyncExternalStore(subscribe, getSnapshot);
}
